import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { SceneFaces } from './SceneFaces';
import { Check, Download, Globe, Languages, Loader2, Play, Plus, RefreshCw, Trash2, X } from 'lucide-react';

const keyOf = (code) => code.replace(/\./g, '_').replace(/\$/g, '_');
const friendly = (d) => (typeof d === 'string' && d.includes('|') ? d.split('|')[1] : d);

function ABPlayer({ scene, version }) {
  const [side, setSide] = useState('translated');
  const src = side === 'original' ? scene.video_path : version.video_path;
  return (
    <div className="rounded-xl overflow-hidden border border-[#10B981]/25 bg-black" data-testid={`lang-video-${version.code}`}>
      <div className="flex items-center gap-1 p-2 bg-[#0a0a0e]">
        {[['original', 'Original'], ['translated', version.label]].map(([k, label]) => (
          <button key={k} data-testid={`lang-ab-${k}-${version.code}`} onClick={() => setSide(k)}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors ${side === k ? 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/40' : 'text-[#8A8A93] border-white/10 hover:text-white'}`}>{label}</button>
        ))}
        <span className="ml-auto text-[10px] text-[#55555C]">{version.mode === 'dub' ? `Dubbed over the approved take${version.speaker_aware ? ' · speaker-aware' : ''}` : 'Native re-render'}</span>
        <a href={getImageUrl(version.video_path)} download className="text-[10px] text-[#00F0FF] hover:underline flex items-center gap-1" data-testid={`lang-download-${version.code}`}><Download className="w-3 h-3" /> Download</a>
      </div>
      <video key={src} controls className="w-full max-h-[300px]" src={getImageUrl(src)} />
    </div>
  );
}

function VoiceRow({ base, scene, characters, version, onScene }) {
  const [voices, setVoices] = useState(null);
  const speakerIds = [...new Set(version.dialogue_lines.map(l => l.character_id).filter(Boolean))];
  useEffect(() => { api.get('/elevenlabs/voices').then(r => setVoices(r.data.voices || [])).catch(() => setVoices([])); }, []);
  const current = (cid) => (version.voice_overrides || {})[cid]
    || (scene.dialogue_lines || []).find(l => l.character_id === cid && l.elevenlabs_voice_id)?.elevenlabs_voice_id
    || characters.find(c => c.id === cid)?.voice_id || '';
  const setVoice = async (cid, vid) => {
    try {
      await api.put(base, { voice_overrides: { ...(version.voice_overrides || {}), [cid]: vid } });
      const r = await api.get(`/projects/${scene.project_id}/scenes/${scene.id}`); onScene(r.data);
    } catch { toast.error('Could not save voice'); }
  };
  if (speakerIds.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid={`lang-voices-${version.code}`}>
      <span className="text-[10px] uppercase tracking-wider text-[#55555C]">Voices for this version</span>
      {speakerIds.map(cid => {
        const name = characters.find(c => c.id === cid)?.name || version.dialogue_lines.find(l => l.character_id === cid)?.character_name || 'Speaker';
        const val = current(cid);
        return (
          <label key={cid} className="flex items-center gap-1.5 text-xs">
            <span className={val ? 'text-[#c8c8d0]' : 'text-[#F59E0B]'}>{name}:</span>
            <select data-testid={`lang-voice-${version.code}-${cid}`} value={val} onChange={e => setVoice(cid, e.target.value)} className={`ff-input text-[11px] py-1 ${val ? '' : 'border-[#F59E0B]/50'}`}>
              <option value="">{voices === null ? 'Loading…' : 'Pick a voice…'}</option>
              {(voices || []).map(v => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}
              {val && voices && !voices.some(v => v.voice_id === val) && <option value={val}>Current voice</option>}
            </select>
          </label>
        );
      })}
    </div>
  );
}

function VersionCard({ projectId, scene, characters, version, onScene }) {
  const [lines, setLines] = useState(version.dialogue_lines);
  const [busy, setBusy] = useState(null);
  const [progress, setProgress] = useState(null);
  const [method, setMethod] = useState('auto');
  const base = `/projects/${projectId}/scenes/${scene.id}/languages/${encodeURIComponent(version.code)}`;
  const refresh = async () => { const r = await api.get(`/projects/${projectId}/scenes/${scene.id}`); onScene(r.data); };
  useEffect(() => { setLines(version.dialogue_lines); }, [version.dialogue_lines]);
  useEffect(() => {
    if (version.status !== 'generating') { setProgress(null); return undefined; }
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`${base}/status`);
        setProgress(data.progress);
        if (data.status !== 'generating') {
          clearInterval(t); await refresh();
          if (data.status === 'done') toast.success(`${version.label} version is ready`);
          else if (data.status === 'needs_mapping') toast.info('One more thing: tell me which face is which speaker');
          else toast.error(data.error || 'Rendering failed');
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [version.status, base]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveLine = async (i) => {
    if ((lines[i]?.text || '') === (version.dialogue_lines[i]?.text || '')) return;
    try { await api.put(base, { dialogue_lines: lines }); await refresh(); } catch { toast.error('Could not save line'); }
  };
  const make = async () => {
    setBusy('make');
    try { await api.post(`${base}/generate`, { mode: method }); await refresh(); }
    catch (err) { toast.error(friendly(err.response?.data?.detail || 'Could not start'), { duration: 7000 }); }
    finally { setBusy(null); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove the ${version.label} version?`)) return;
    try { await api.delete(base); await refresh(); } catch { toast.error('Could not remove'); }
  };
  const generating = version.status === 'generating';
  const faceMapCurrent = scene.face_map && scene.face_map.video_path === scene.video_path ? scene.face_map : null;

  return (
    <div className="rounded-2xl border border-white/[0.08] bg-[#141418] p-4 space-y-3" data-testid={`lang-version-${version.code}`}>
      <div className="flex items-center gap-2">
        <Globe className="w-4 h-4 text-[#00F0FF]" />
        <p className="text-sm font-semibold text-white">{version.label}</p>
        {version.status === 'done' && <span className="text-[10px] px-2 py-0.5 rounded-full border bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30" data-testid={`lang-status-${version.code}`}>Ready</span>}
        {generating && <span className="text-[10px] text-[#C4B5FD] flex items-center gap-1" data-testid={`lang-status-${version.code}`}><Loader2 className="w-3 h-3 animate-spin" /> {progress || 'Rendering…'}</span>}
        {version.status === 'failed' && <span className="text-[10px] text-red-400 truncate max-w-[300px]" title={version.error} data-testid={`lang-status-${version.code}`}>{friendly(version.error) || 'Failed'}</span>}
        <button data-testid={`lang-delete-${version.code}`} onClick={remove} className="ml-auto p-1.5 rounded-lg text-[#55555C] hover:text-red-400 hover:bg-red-500/10" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>

      <div className="space-y-1.5">
        <p className="text-[10px] uppercase tracking-wider text-[#55555C]">Dialogue — edit anything that reads wrong; the take uses exactly these lines</p>
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs" data-testid={`lang-line-${version.code}-${i}`}>
            <div className="p-2 rounded-lg bg-black/30 border border-white/[0.04]"><span className="text-[#8A8A93] font-semibold">{l.character_name || 'Speaker'}:</span> <span className="text-[#c8c8d0]">{l.original_text}</span></div>
            <textarea data-testid={`lang-edit-${version.code}-${i}`} value={l.text} rows={2} disabled={generating}
              onChange={e => setLines(prev => prev.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} onBlur={() => saveLine(i)}
              className="ff-input text-xs resize-none border-[#00F0FF]/20 focus:border-[#00F0FF]/50 disabled:opacity-60" />
          </div>
        ))}
      </div>

      {method !== 'regenerate' && <VoiceRow base={base} scene={scene} characters={characters} version={version} onScene={onScene} />}

      {version.status === 'needs_mapping' && (
        <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3 space-y-2" data-testid={`lang-needs-mapping-${version.code}`}>
          <p className="text-xs text-[#F59E0B] font-semibold">{friendly(version.error) || 'Tell me which face is which speaker'}</p>
          <SceneFaces projectId={projectId} scene={scene} characters={characters} onScene={onScene} />
        </div>
      )}

      {!generating && (
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid={`lang-make-${version.code}`} onClick={make} disabled={!!busy}
            className="ff-btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
            {busy === 'make' ? <Loader2 className="w-4 h-4 animate-spin" /> : version.video_path ? <RefreshCw className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {version.status === 'needs_mapping' ? 'Continue' : version.video_path ? `Remake ${version.label} version` : `Make ${version.label} version`}
          </button>
          <select data-testid={`lang-method-${version.code}`} value={method} onChange={e => setMethod(e.target.value)} className="ff-input text-[11px] py-1.5" title="How the translated take is produced">
            <option value="auto">Method: Auto</option>
            <option value="dub" disabled={!scene.video_path}>Dub over approved take (picture unchanged)</option>
            <option value="regenerate">Re-render with native voices</option>
          </select>
          {faceMapCurrent && Object.keys(faceMapCurrent.mapping || {}).length > 0 && version.status !== 'needs_mapping' && (
            <span className="text-[10px] text-[#10B981] flex items-center gap-1"><Check className="w-3 h-3" /> speakers mapped to faces</span>
          )}
        </div>
      )}

      {version.video_path && version.status === 'done' && <ABPlayer scene={scene} version={version} />}
    </div>
  );
}

// Slide-over: pick a language → translation appears (editable) → one button makes the translated take.
export function SceneLanguages({ projectId, scene, characters = [], onScene, onClose }) {
  const [presets, setPresets] = useState([]);
  const [code, setCode] = useState('pt-BR');
  const [custom, setCustom] = useState('');
  const [adding, setAdding] = useState(false);
  const versions = Object.values(scene.language_versions || {});
  const hasDialogue = (scene.dialogue_lines || []).some(l => (l.text || '').trim());
  useEffect(() => { api.get('/languages/presets').then(r => setPresets(r.data.presets || [])).catch(() => {}); }, []);

  const add = async () => {
    const chosen = code === 'custom' ? { code: custom.trim().toLowerCase().replace(/\s+/g, '-'), label: custom.trim() } : { code, label: presets.find(p => p.code === code)?.label };
    if (!chosen.code) { toast.error('Enter a language'); return; }
    if (scene.language_versions?.[keyOf(chosen.code)]) { toast.info('That language is already here'); return; }
    setAdding(true);
    try {
      await api.post(`/projects/${projectId}/scenes/${scene.id}/languages`, chosen, { timeout: 120000 });
      const r = await api.get(`/projects/${projectId}/scenes/${scene.id}`); onScene(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Translation failed'); }
    finally { setAdding(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-[2px]" data-testid="scene-languages" onClick={onClose}>
      <div className="absolute right-0 top-0 h-full w-full max-w-[760px] bg-[#0E0E12] border-l border-white/10 shadow-2xl flex flex-col animate-slide-in-right" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-start gap-3">
          <Languages className="w-5 h-5 text-[#00F0FF] mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white">Languages · {scene.title || 'Scene'}</p>
            <p className="text-[11px] text-[#8A8A93]">Pick a language, tweak the lines if needed, press one button. Same picture, new language.</p>
          </div>
          <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-white/10 text-[#8A8A93]" data-testid="scene-languages-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div className="flex items-center gap-2 flex-wrap p-3 rounded-2xl bg-[#141418] border border-white/[0.06]">
            <select data-testid="lang-select" value={code} onChange={e => setCode(e.target.value)} className="ff-input text-sm py-2">
              {presets.map(p => <option key={p.code} value={p.code}>{p.label}</option>)}
              <option value="custom">Other…</option>
            </select>
            {code === 'custom' && <input data-testid="lang-custom" value={custom} onChange={e => setCustom(e.target.value)} placeholder="e.g. Swedish" className="ff-input text-sm py-2 w-36" />}
            <button data-testid="lang-add" onClick={add} disabled={adding || !hasDialogue} title={hasDialogue ? '' : 'Add dialogue to this scene first'}
              className="ff-btn-primary text-sm flex items-center gap-2 disabled:opacity-50">{adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {adding ? 'Translating…' : 'Add language'}</button>
            {!scene.video_path && <p className="text-[10px] text-[#F59E0B] w-full">Generate the original take first — translated versions are built from it.</p>}
          </div>
          {versions.length === 0 && <p className="text-xs text-[#55555C] text-center py-6">No translated versions yet.</p>}
          {versions.map(v => <VersionCard key={v.code} projectId={projectId} scene={scene} characters={characters} version={v} onScene={onScene} />)}
        </div>
      </div>
    </div>,
    document.body
  );
}
